"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import Button from "@/components/ui/button";
import { getCycleColor } from "@/lib/care-plan-cycle-colors";
import { flattenStepsWithBranchChildren, isStepTerminallyComplete } from "@/lib/care-plan-cycle-utils";
import { cx } from "@/lib/tailwind-utils";
import type { CarePlanCycleDetail } from "@/types/care-plan";
import { useCarePlanContext } from "./care-plan";
import { InlineNameInput } from "./inline-name-input";
import { type CarePlanStepBase, isPatientStep } from "./types";
import type { CarePlanStep } from "./types";
interface CycleProgress {
  cycle: CarePlanCycleDetail;
  completedPasses: number;
  totalPasses: number;
}
function formatGap(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  if (minutes < 10080) return `${Math.round(minutes / 1440)}d`;
  return `${Math.round(minutes / 10080)}w`;
}
function CycleName(t0) {
  const $ = _c(8);
  const {
    cycle,
    fallbackName
  } = t0;
  const {
    config: t1,
    cycles: t2
  } = useCarePlanContext();
  const {
    readOnly
  } = t1;
  const {
    onRenameCycle
  } = t2;
  const displayName = cycle.name || fallbackName;
  const canEdit = !readOnly && onRenameCycle;
  const t3 = !canEdit;
  let t4;
  if ($[0] !== canEdit || $[1] !== cycle.id || $[2] !== onRenameCycle) {
    t4 = value => {
      if (canEdit) {
        onRenameCycle(cycle.id, value);
      }
    };
    $[0] = canEdit;
    $[1] = cycle.id;
    $[2] = onRenameCycle;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== displayName || $[5] !== t3 || $[6] !== t4) {
    t5 = <InlineNameInput className="text-body-strong text-foreground w-44" value={displayName} readOnly={t3} onChange={t4} />;
    $[4] = displayName;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function CycleProgressList() {
  const $ = _c(22);
  const {
    state: t0,
    cycles: t1
  } = useCarePlanContext();
  const {
    steps
  } = t0;
  const {
    displayCycles,
    cycleFamilyMap,
    highlightedCycleId,
    setHighlightedCycleId,
    onlyShowCurrentPasses,
    toggleOnlyShowCurrentPasses
  } = t1;
  let t2;
  if ($[0] !== cycleFamilyMap || $[1] !== displayCycles || $[2] !== steps) {
    const allSteps = flattenStepsWithBranchChildren(steps);
    t2 = displayCycles.map(cycle => {
      const familyIds = cycleFamilyMap.get(cycle.id) ?? new Set([cycle.id]);
      const cycleSteps = allSteps.filter(s => "cycleId" in s && familyIds.has((s as CarePlanStepBase & {
        cycleId: string | null;
      }).cycleId ?? ""));
      const passCounts = new Map();
      for (const step of cycleSteps) {
        if (!isPatientStep(step)) {
          continue;
        }
        const stepCycleId = (step as CarePlanStep & {
          cycleId: string | null;
        }).cycleId ?? "";
        const pass = (step as CarePlanStep & {
          cyclePass: number | null;
        }).cyclePass ?? 1;
        const key = `${stepCycleId}:${pass}`;
        const existing = passCounts.get(key) ?? {
          total: 0,
          completed: 0,
          pass
        };
        existing.total = existing.total + 1;
        if (isStepTerminallyComplete(step)) {
          existing.completed = existing.completed + 1;
        }
        passCounts.set(key, existing);
      }
      let completedPasses = 0;
      for (const counts of Array.from(passCounts.values())) {
        if (counts.total > 0 && counts.completed === counts.total) {
          completedPasses = completedPasses + 1;
        }
      }
      const totalPasses = cycle.passCount * familyIds.size;
      return {
        cycle,
        completedPasses,
        totalPasses
      };
    });
    $[0] = cycleFamilyMap;
    $[1] = displayCycles;
    $[2] = steps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const progressData = t2;
  if (progressData.length === 0) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-caption-emphasis text-foreground-secondary">Cycles</p>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onlyShowCurrentPasses) {
    t4 = onlyShowCurrentPasses ? <RiEyeOffLine size={13} /> : <RiEyeLine size={13} />;
    $[5] = onlyShowCurrentPasses;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = onlyShowCurrentPasses ? "Show all passes" : "Show only the current pass";
  const t6 = onlyShowCurrentPasses ? "Current pass" : "All passes";
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6 || $[10] !== toggleOnlyShowCurrentPasses) {
    t7 = <div className="flex items-center justify-between">{t3}<Button onClick={toggleOnlyShowCurrentPasses} variant="ghost" size="sm" className="text-foreground-secondary" icon={t4} tooltip={t5}>{t6}</Button></div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = toggleOnlyShowCurrentPasses;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== highlightedCycleId || $[13] !== progressData || $[14] !== setHighlightedCycleId) {
    let t9;
    if ($[16] !== highlightedCycleId || $[17] !== setHighlightedCycleId) {
      t9 = (item, index) => {
        const color = getCycleColor(index);
        const percent = item.totalPasses > 0 ? Math.round(item.completedPasses / item.totalPasses * 100) : 0;
        const isHighlighted = highlightedCycleId === item.cycle.id;
        return <div key={item.cycle.id}><Button variant="ghost" className={cx("w-full text-left cursor-pointer rounded-md transition-colors flex-col items-start", isHighlighted && "bg-hover")} onClick={() => setHighlightedCycleId(isHighlighted ? null : item.cycle.id)}><div className="flex items-center gap-2"><span className="inline-block size-2.5 rounded-full shrink-0" style={{
                backgroundColor: color.border
              }} /><CycleName cycle={item.cycle} fallbackName={`Cycle ${index + 1}`} />{item.cycle.gapMinutes > 0 && <span className="text-caption text-foreground-secondary">{formatGap(item.cycle.gapMinutes)} gap</span>}</div><div className="h-1.5 w-full rounded-full bg-background-secondary overflow-hidden mt-1.5"><div className="h-full rounded-full transition-all duration-300" style={{
                width: `${percent}%`,
                backgroundColor: color.border
              }} /></div><p className="text-caption text-foreground-secondary mt-1">{item.completedPasses} of {item.totalPasses} passes complete</p></Button></div>;
      };
      $[16] = highlightedCycleId;
      $[17] = setHighlightedCycleId;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    t8 = progressData.map(t9);
    $[12] = highlightedCycleId;
    $[13] = progressData;
    $[14] = setHighlightedCycleId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <div className="space-y-3">{t7}{t8}</div>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}